import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import BroadcastMessageBody from '@/components/BroadcastMessageBody';

type PopupRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  displayMode?: 'once' | 'sticky_daily';
  expiresAt?: string | null;
};

/**
 * In-app popups: `once` uses OK to mark seen on server; `sticky_daily` is re-fetched each load (shows again
 * after reload) until expiresAt; OK only hides for the current page session; Don't show again (server).
 */
export default function KioskInAppPopupGate() {
  const [queue, setQueue] = useState<PopupRow[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [marking, setMarking] = useState(false);
  const batchTotalRef = useRef(0);

  const load = useCallback(async () => {
    // Avoid noisy 401s when user is not authenticated yet.
    // The gate will re-run automatically when the token changes (login/logout).
    if (!apiService.getAccessToken()) {
      batchTotalRef.current = 0;
      setQueue([]);
      setActiveIndex(0);
      return;
    }
    try {
      const res = await apiService.getMyUnseenInAppPopups();
      if (res.success && res.data?.popups?.length) {
        const list = res.data.popups;
        batchTotalRef.current = list.length;
        setQueue(list);
        setActiveIndex(0);
      } else {
        batchTotalRef.current = 0;
        setQueue([]);
        setActiveIndex(0);
      }
    } catch {
      batchTotalRef.current = 0;
      setQueue([]);
      setActiveIndex(0);
    }
  }, []);

  useEffect(() => {
    void load();
    const onAuth = () => void load();
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [load]);

  const current = queue[activeIndex];
  const isSticky = current?.displayMode === 'sticky_daily';

  const removeCurrentFromQueue = () => {
    setQueue((prev) => {
      const next = prev.filter((_, i) => i !== activeIndex);
      setActiveIndex((idx) => {
        if (next.length === 0) return 0;
        return Math.min(idx, next.length - 1);
      });
      return next;
    });
  };

  const onMarkSeen = async () => {
    if (!current || marking || isSticky) return;
    setMarking(true);
    try {
      const res = await apiService.markInAppPopupSeen(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  const onCloseSticky = () => {
    if (!current || marking || !isSticky) return;
    removeCurrentFromQueue();
  };

  const onNeverAgain = async () => {
    if (!current || marking || !isSticky) return;
    setMarking(true);
    try {
      const res = await apiService.markInAppPopupNeverAgain(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  if (!current) return null;

  const batchTotal = batchTotalRef.current;
  const positionInBatch = activeIndex + 1;

  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/62 p-4 backdrop-blur-[1.5px]" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="kiosk-in-app-popup-title"
        aria-describedby={batchTotal > 1 ? 'kiosk-in-app-popup-step' : undefined}
        className="flex max-h-[85vh] w-full max-w-[760px] flex-col overflow-hidden rounded-2xl border border-white/14 bg-gradient-to-b from-[#071a4a] to-[#050f2e] text-white shadow-[0_18px_44px_rgba(0,0,0,0.42)]"
        style={{ animation: 'kioskPopupEnter 180ms ease-out' }}
      >
        <style>{`@keyframes kioskPopupEnter { from { opacity: 0; transform: translateY(8px) scale(0.99); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-7 py-6">
          <h2 id="kiosk-in-app-popup-title" className="min-w-0 flex-1 truncate text-2xl font-semibold leading-8 text-white">
            {current.title || 'Announcement'}
          </h2>
          {batchTotal > 1 ? (
            <p id="kiosk-in-app-popup-step" className="shrink-0 rounded-md bg-white/8 px-2.5 py-1 text-[10px] font-medium tabular-nums text-white/75">
              {positionInBatch} of {batchTotal}
            </p>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-7 py-5">
          {isSticky && current.expiresAt ? (
            <p className="mb-3 text-xs text-white/45">
              Ends{' '}
              {new Date(current.expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          ) : null}
          <div className="whitespace-pre-wrap text-base leading-7 text-white/85">
            <BroadcastMessageBody
              text={current.body}
              linkClassName="font-medium text-cyan-300 underline decoration-cyan-300/70 underline-offset-2 hover:text-cyan-200 break-all"
            />
          </div>
        </div>

        <div className="px-7 pb-6 pt-4">
          {isSticky ? (
            <>
              <button
                type="button"
                onClick={() => onCloseSticky()}
                disabled={marking}
                className="mx-auto block w-1/3 max-w-full rounded-xl bg-[var(--Yellow-Primary,#FC0)] px-4 py-3.5 text-base font-semibold text-[#0b1437] transition duration-150 hover:brightness-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
              >
                OK
              </button>
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => void onNeverAgain()}
                  disabled={marking}
                  className="text-[11px] text-white/35 underline decoration-white/20 underline-offset-2 transition duration-150 hover:text-white/50 disabled:opacity-40"
                >
                  Don&apos;t show this again
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              onClick={() => void onMarkSeen()}
              disabled={marking}
              className="mx-auto block w-1/3 max-w-full rounded-xl bg-[var(--Yellow-Primary,#FC0)] px-4 py-3.5 text-base font-semibold text-[#0b1437] transition duration-150 hover:brightness-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {marking ? 'Saving...' : 'OK'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
